import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/useLanguage";

export const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userType, setUserType] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast({
        title: t("auth.signup.toast.termsRequired.title"),
        description: t("auth.signup.toast.termsRequired.desc"),
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t("auth.signup.toast.passwordsDontMatch.title"),
        description: t("auth.signup.toast.passwordsDontMatch.desc"),
        variant: "destructive",
      });
      return;
    }

    if (!userType) {
      toast({
        title: t("auth.signup.toast.userTypeRequired.title"),
        description: t("auth.signup.toast.userTypeRequired.desc"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/login?emailConfirmed=true`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            user_type: userType,
          }
        }
      });

      if (error) {
        toast({
          title: t("auth.signup.toast.signupFailed.title"),
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("auth.signup.toast.success.title"),
        description: t("auth.signup.toast.success.desc"),
      });

      navigate("/login");
      
    } catch (error) {
      toast({
        title: t("auth.signup.toast.error.title"),
        description: t("auth.signup.toast.error.desc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 bg-gradient-medical opacity-5"></div>
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-accent/10 rounded-full blur-2xl"></div>
      
      <Card className="w-full max-w-lg shadow-elegant border-border/30 backdrop-blur-sm bg-card/95 relative z-10">
        <CardHeader className="text-center space-y-6 pb-8">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-primary/10 p-1">
              <img 
                src="/assets/icon.png" 
                alt="NostriMed Logo" 
                className="w-full h-full object-contain rounded-full"
                width="80"
                height="80"
                loading="lazy"
              />
            </div>
          </div>
          <div className="space-y-2">
            <CardTitle className="text-3xl font-bold bg-gradient-medical bg-clip-text text-transparent">
              {t("auth.signup.title")}
            </CardTitle>
            <CardDescription className="text-muted-foreground text-base">
              {t("auth.signup.subtitle")}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-8 px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label htmlFor="firstName" className="text-sm font-semibold text-foreground">
                  {t("auth.signup.firstName")}
                </Label>
                <Input
                  id="firstName"
                  placeholder={t("auth.signup.firstNamePlaceholder")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="h-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="lastName" className="text-sm font-semibold text-foreground">
                  {t("auth.signup.lastName")}
                </Label>
                <Input
                  id="lastName"
                  placeholder={t("auth.signup.lastNamePlaceholder")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="h-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-semibold text-foreground">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="userType" className="text-sm font-semibold text-foreground">
                {t("auth.signup.userTypeLabel")}
              </Label>
              <Select value={userType} onValueChange={setUserType} required>
                <SelectTrigger className="h-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50">
                  <SelectValue placeholder={t("auth.signup.userTypePlaceholder")} />
                </SelectTrigger>
                <SelectContent className="bg-card border-border/50">
                  <SelectItem value="student" className="focus:bg-primary/10">
                    {t("auth.signup.userType.student")}
                  </SelectItem>
                  <SelectItem value="resident" className="focus:bg-primary/10">
                    {t("auth.signup.userType.resident")}
                  </SelectItem>
                  <SelectItem value="professional" className="focus:bg-primary/10">
                    {t("auth.signup.userType.professional")}
                  </SelectItem>
                  <SelectItem value="educator" className="focus:bg-primary/10">
                    {t("auth.signup.userType.educator")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-3">
                <Label htmlFor="password" className="text-sm font-semibold text-foreground">
                  {t("auth.signup.password")}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.signup.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-10 w-10 hover:bg-primary/10"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-foreground">
                  {t("auth.signup.confirmPassword")}
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("auth.signup.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-12 pr-12 border-2 border-border/50 focus:border-primary transition-all duration-300 focus:shadow-medical bg-background/50"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-10 w-10 hover:bg-primary/10"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {t("auth.signup.termsPrefix")} <Link to="/terms" className="text-primary hover:text-primary-glow transition-colors font-medium underline-offset-2 hover:underline">{t("auth.signup.terms")}</Link> {t("auth.signup.and")} <Link to="/privacy" className="text-primary hover:text-primary-glow transition-colors font-medium underline-offset-2 hover:underline">{t("auth.signup.privacy")}</Link>
              </Label>
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full h-12 text-base font-semibold"
              disabled={isLoading}
            >
              {isLoading ? t("auth.signup.creating") : t("auth.signup.createButton")}
            </Button>
          </form>

          <div className="text-center pt-4">
            <p className="text-sm text-muted-foreground">
              {t("auth.signup.alreadyHaveAccount")} <Link
                to="/login"
                className="text-primary hover:text-primary-glow transition-colors font-semibold underline-offset-2 hover:underline"
              >
                {t("auth.signup.signIn")}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};